import React, { useEffect, useState } from "react";

import { Msg } from "src/i18n/Msg";
import { t } from "src/utils/i18nText";
import { Router } from "src/navigation/Router";
import { getTgAccessToken, saveTgAccessToken } from "src/api";
import { telegramWebAppAuth } from "src/api/user/auth";
import {
  hasTelegramWebApp,
  prepareTelegramWebApp,
  waitForTelegramInitData,
} from "src/utils/telegramWebApp";
import { ThemeProvider } from "src/context/ThemeContext";


export const App: React.FC = () => {
  const [tgAuthReady, setTgAuthReady] = useState(true);
  const [tgAuthError, setTgAuthError] = useState<string>("");

  useEffect(() => {
    let isMounted = true;

    const run = async () => {
      prepareTelegramWebApp();

      if (!hasTelegramWebApp()) {
        return;
      }
      const existing = getTgAccessToken();
      if (existing) {
        return;
      }

      // Ждём данные запуска, НЕ трогая интерфейс: скрипт Telegram подключён на
      // всех страницах, поэтому в обычном браузере ожидание закончится ничем и
      // показывать «Авторизация через Telegram…» там незачем.
      const initData = await waitForTelegramInitData();

      if (!initData || !isMounted) {
        return;
      }

      setTgAuthReady(false);
      setTgAuthError("");

      try {
        // Разовый сбой сети или бэкенда не должен оставлять пользователя
        // неавторизованным: повторяем попытку с нарастающей паузой.
        let lastError: unknown = null;

        for (let attempt = 0; attempt < 3; attempt += 1) {
          try {
            const resp: any = await telegramWebAppAuth(initData);
            const access: string | undefined = resp?.data?.access;

            if (!access) {
              throw new Error("No access token in response");
            }

            saveTgAccessToken(access);
            return;
          } catch (err) {
            lastError = err;

            // Telegram отверг данные запуска — повторять бессмысленно.
            const status = (err as any)?.response?.status;
            if (status && status >= 400 && status < 500) {
              break;
            }

            await new Promise((resolve) =>
              window.setTimeout(resolve, 700 * (attempt + 1))
            );
          }
        }

        throw lastError ?? new Error("Telegram auth failed");
      } catch (err) {
        console.error("Telegram WebApp auth failed:", err);
        if (isMounted) {
          setTgAuthError(t("components.App.telegramAuth.error"));
        }
      } finally {
        if (isMounted) {
          setTgAuthReady(true);
        }
      }
    };

    run();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <ThemeProvider>
      <div className="app" data-testid="page">
        {tgAuthReady ? (
          <Router />
        ) : (
          <div style={{ padding: 16, color: "var(--page-text)" }}>
            <Msg id="components.App.telegramAuth.loading" />
          </div>
        )}
        {tgAuthError ? (
          <div style={{ padding: 16, color: "red" }}>{tgAuthError}</div>
        ) : null}
      </div>
    </ThemeProvider>
  );
};
